import { useMemo, useEffect, useState, MouseEvent, ReactEventHandler } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { isNil, uniq } from 'lodash';

import { Icon, IconName } from '@/components/icon';
import { getDomain } from '@/public/utils/url';

import { getFootnoteListId, getIcon } from './utils';

import styles from './footnote.module.scss';

interface Footnote {
  citeId: string;
  label: string;
  pages: string;
  href: string;
  icon: IconName;
  num: number;
}

export const FootnoteList = ({ editor }: NodeViewProps) => {
  const [isOpened, setIsOpened] = useState(false);
  const [footnotes, setFootnotes] = useState<Footnote[]>([]);

  useEffect(() => {
    const generateFootnotes = () => {
      const footnoteNodes: Footnote[] = [];
      editor.state.doc.descendants((node) => {
        if (node.type.name !== 'footnoteNode') {
          return;
        }

        const href = node.attrs.href;
        const domain = isNil(href) || href.length === 0 ? null : getDomain(href);
        if (
          domain !== null &&
          !footnoteNodes.some((footnote) => footnote.citeId === node.attrs.citeId)
        ) {
          footnoteNodes.push({
            href,
            citeId: node.attrs.citeId,
            pages: node.attrs.pages,
            label: node.attrs.label ?? `Link to ${domain}`,
            icon: getIcon(domain),
            num: node.attrs.num,
          });
        }
      });
      setFootnotes(footnoteNodes);
    };

    generateFootnotes();

    editor.on('update', generateFootnotes);

    return () => {
      editor.off('update', generateFootnotes);
    };
  }, [editor]);

  const uniqueHostIcons = useMemo(() => uniq(footnotes.map((note) => note.icon)), [footnotes]);

  const handleToggle: ReactEventHandler<HTMLDetailsElement> = (event) => {
    setIsOpened((event.target as HTMLDetailsElement).open);
  };

  const handleClick = (event: MouseEvent) => {
    // Prevent selecting cell
    event.stopPropagation();
  };

  if (footnotes.length === 0) {
    return <></>;
  }

  return (
    <NodeViewWrapper className={styles.footnoteListNode}>
      <details className={styles.details} onToggle={handleToggle} onClick={handleClick}>
        {isOpened ? (
          <summary>
            Sources
            <Icon name="ChevronUp" size={16} />
          </summary>
        ) : (
          <summary>
            Sources ({footnotes.length})
            {uniqueHostIcons.map((icon) => (
              <Icon key={icon} name={icon} size={16} />
            ))}
            <Icon name="ChevronDown" size={16} />
          </summary>
        )}

        <ol className={styles.footnotesList} data-custom-styles>
          {footnotes.map((footnote, index) => {
            const pages =
              footnote.pages !== undefined ? String(footnote.pages).split(',') : undefined;
            return (
              <li
                key={index}
                id={getFootnoteListId(footnote.citeId)}
                className={styles.footnoteItem}
                data-num={footnote.num}>
                <a
                  href={footnote.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  draggable={false}
                  className={styles.listItem}>
                  <Icon name={footnote.icon} size={20} />
                  <div>
                    <span className={styles.footnoteLabel}>{footnote.label}</span>
                    {pages && pages.length > 0 && (
                      <>
                        &nbsp;
                        <span className={styles.pageNumber}>
                          Page{pages.length > 1 ? 's' : ''} {pages.join(', ')}
                        </span>
                      </>
                    )}
                  </div>
                </a>
              </li>
            );
          })}
        </ol>
      </details>
    </NodeViewWrapper>
  );
};
